// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CreateMapByObject } from '@shared/utils/CreateMapByObjectUtils';
import { DirectEntitlementProductSubscriptionResponseMessage } from '@shared/utils/DirectMessages/DirectMessagesImport';
export class EntitlementHistory {
    constructor(id, name) {
        this.ReqType = null;
        this.ReqStatus = null;
        this.ReqTime = null;
        this.Comment = null;
        this.Id = id;
        this.Name = name;
    }
    UpdateHistoryStatus(responseMessage) {
        this.ReqType = responseMessage.ReqType;
        this.ReqStatus = responseMessage.ReqStatus;
        this.ReqTime = responseMessage.ReqTime;
        this.Comment = responseMessage.Comment;
    }
}
export class EntitlementProduct {
    constructor(message) {
        this.Id = null;
        this.Name = null;
        this.Description = null;
        this.Category = null;
        this.CurrencyId = null;
        this.ProPrice = null;
        this.NonProPrice = null;
        this.HasDocument = null;
        this.ImageBytes = null;
        this.DocumentArray = null; // save here last documents when subscribe via settings panel (#109924)
        this.ProductDataType = null; // value from EntitlementProductDataType
        this.ShowInSettings = null; // only for products with ProductDataType = EntitlementProductDataType.Notifications
        this.ColorHEX = null; // color for Notifications panel
        this.ReqType = EntitlementProductRequestType.None;
        this.ReqStatus = EntitlementProductRequestStatus.None;
        this.ReqTime = null;
        this.ReqTimeCreated = null;
        this.isNewProduct = false;
        this.isSubscribeActionDone = false;
        this.isWaitForUnsubscribe = false;
        this.isUnsubscribeActionDone = false;
        this.isWaitForSubscribe = false;
        this.StatusUpdateHandler = null;
        this.IsUnsubscribeAllowed = null;
        this.IsActive = true;
        this.QuoteDelay = 0;
        this.UpdateProduct(message);
    }
    UpdateProduct(message) {
        this.Id = message.Id;
        this.Name = message.Name;
        this.Description = message.Description;
        this.Category = message.Category;
        this.CurrencyId = message.CurrencyId;
        this.ProPrice = message.ProPrice;
        this.NonProPrice = message.NonProPrice;
        this.HasDocument = message.HasDocument;
        this.ImageBytes = message.ImageBytes;
        this.ProductDefinitions = message.ProductDefinitions;
        this.IsUnsubscribeAllowed = message.IsUnsubscribeAllowed;
        this.IsActive = message.IsActive;
        this.ProductDataType = message.ProductDataType;
        this.ShowInSettings = message.ShowInSettings;
        this.ColorHEX = message.ColorHEX;
        this.QuoteDelay = message.QuoteDelay;
    }
    UpdateProductStatus(message) {
        this.ReqType = message.ReqType;
        this.ReqStatus = message.ReqStatus;
        this.ReqTime = message.ReqTime;
        this.ReqTimeCreated = new Date(message.ReqTime);
        this.Comment = message.Comment;
        this.isNewProduct =
            this.ReqStatus === EntitlementProductRequestStatus.None &&
                this.ReqType === EntitlementProductRequestType.None;
        if (!this.isSubscribeActionDone) {
            this.isSubscribeActionDone =
                this.ReqStatus === EntitlementProductRequestStatus.Approved &&
                    this.ReqType === EntitlementProductRequestType.Subscribe;
        }
        this.isWaitForUnsubscribe =
            this.ReqStatus === EntitlementProductRequestStatus.Processing &&
                this.ReqType === EntitlementProductRequestType.Unsubscribe;
        this.isUnsubscribeActionDone =
            this.ReqStatus === EntitlementProductRequestStatus.Approved &&
                this.ReqType === EntitlementProductRequestType.Unsubscribe;
        this.isWaitForSubscribe =
            this.ReqStatus === EntitlementProductRequestStatus.Processing &&
                this.ReqType === EntitlementProductRequestType.Subscribe;
        if (this.isUnsubscribeActionDone) {
            this.isSubscribeActionDone =
                this.ReqStatus === EntitlementProductRequestStatus.Approved &&
                    this.ReqType === EntitlementProductRequestType.Subscribe;
        } // что-то не так с этим if-ом (во-первых, он такой же как на несколько строк выше. Во-вторых, сюда можно попасть только если isUnsubscribeActionDone == true но тогда внутри if-а всегда будет сетится false )
        this.isSubscribeActionDone =
            this.isSubscribeActionDone || // а вот такое условие ИМХО стоит добавить иначе продукт не считается Subscribed во время ожидания обработки на отписку (отправили запрос на отписку, но отписка еще не совершена -> но подписка же была когда-то выполнена)
                (this.ReqStatus === EntitlementProductRequestStatus.Processing &&
                    this.ReqType === EntitlementProductRequestType.Unsubscribe);
        if (this.StatusUpdateHandler) {
            this.StatusUpdateHandler();
        }
    }
    IsSubscribed() {
        return this.isSubscribeActionDone;
    }
    IsProcessing() {
        return this.isWaitForSubscribe || this.isWaitForUnsubscribe;
    }
    IsAvailableForSubscription() {
        return !this.IsSubscribed(); // #108196
    }
    IsBelongToFilter(entitlementProductFilter) {
        if (!this.IsActive) {
            return false;
        }
        switch (entitlementProductFilter) {
            case EntitlementProductFilter.All:
                return true; // is correct statement?
            case EntitlementProductFilter.Processing:
                return this.IsProcessing();
            case EntitlementProductFilter.Subscribed:
                return this.IsSubscribed();
            case EntitlementProductFilter.AvailableForSubscription:
                return this.IsAvailableForSubscription();
            case EntitlementProductFilter.SubscribedAndProcessing:
                return this.isWaitForUnsubscribe; // или можно иначе: this.IsSubscribed() && this.IsProcessing()
            case EntitlementProductFilter.NotSubscribedButProcessing:
                return this.isWaitForSubscribe; // или можно иначе: !this.IsSubscribed() && this.IsProcessing()
        }
        return false;
    }
    GenerateSubscriptionResponseMessage() {
        const message = new DirectEntitlementProductSubscriptionResponseMessage();
        message.Id = this.Id;
        message.ReqStatus = this.ReqStatus;
        message.ReqType = this.ReqType;
        message.ReqTime = this.ReqTime;
        message.Comment = this.Comment;
        return message;
    }
}
export var EntitlementProductDataType;
(function (EntitlementProductDataType) {
    EntitlementProductDataType[EntitlementProductDataType["Market"] = 0] = "Market";
    EntitlementProductDataType[EntitlementProductDataType["Fundamental"] = 1] = "Fundamental";
    EntitlementProductDataType[EntitlementProductDataType["Notifications"] = 2] = "Notifications";
    EntitlementProductDataType[EntitlementProductDataType["Webhooks"] = 3] = "Webhooks";
})(EntitlementProductDataType || (EntitlementProductDataType = {}));
export var EntitlementProductRequestType;
(function (EntitlementProductRequestType) {
    EntitlementProductRequestType[EntitlementProductRequestType["None"] = -1] = "None";
    EntitlementProductRequestType[EntitlementProductRequestType["Subscribe"] = 0] = "Subscribe";
    EntitlementProductRequestType[EntitlementProductRequestType["Unsubscribe"] = 1] = "Unsubscribe";
    EntitlementProductRequestType[EntitlementProductRequestType["Cancel"] = 2] = "Cancel";
})(EntitlementProductRequestType || (EntitlementProductRequestType = {}));
export const EntitlementProductRequestTypeMap = CreateMapByObject(EntitlementProductRequestType);
export var EntitlementProductRequestStatus;
(function (EntitlementProductRequestStatus) {
    EntitlementProductRequestStatus[EntitlementProductRequestStatus["None"] = -1] = "None";
    EntitlementProductRequestStatus[EntitlementProductRequestStatus["Processing"] = 0] = "Processing";
    EntitlementProductRequestStatus[EntitlementProductRequestStatus["Approved"] = 1] = "Approved";
    EntitlementProductRequestStatus[EntitlementProductRequestStatus["Rejected"] = 2] = "Rejected";
    EntitlementProductRequestStatus[EntitlementProductRequestStatus["Canceled"] = 3] = "Canceled";
})(EntitlementProductRequestStatus || (EntitlementProductRequestStatus = {}));
export const EntitlementProductRequestStatusMap = CreateMapByObject(EntitlementProductRequestStatus);
export var EntitlementProductFilter;
(function (EntitlementProductFilter) {
    EntitlementProductFilter[EntitlementProductFilter["All"] = -1] = "All";
    EntitlementProductFilter[EntitlementProductFilter["Processing"] = 0] = "Processing";
    EntitlementProductFilter[EntitlementProductFilter["Subscribed"] = 1] = "Subscribed";
    EntitlementProductFilter[EntitlementProductFilter["AvailableForSubscription"] = 2] = "AvailableForSubscription";
    EntitlementProductFilter[EntitlementProductFilter["SubscribedAndProcessing"] = 3] = "SubscribedAndProcessing";
    EntitlementProductFilter[EntitlementProductFilter["NotSubscribedButProcessing"] = 4] = "NotSubscribedButProcessing"; // waiting for   subscribe approve (not in combobox's variants, but may be usefull)
})(EntitlementProductFilter || (EntitlementProductFilter = {}));
export function getEntitlementProductFilterKey(entitlementProductFilter) {
    switch (entitlementProductFilter) {
        case EntitlementProductFilter.All:
            return 'screen.products.toolbar.filterCB.All';
        case EntitlementProductFilter.Subscribed:
            return 'screen.products.toolbar.filterCB.Subscribed';
        case EntitlementProductFilter.AvailableForSubscription:
            return 'screen.products.toolbar.filterCB.AvailableForSubscription';
        case EntitlementProductFilter.Processing:
            return 'screen.products.toolbar.filterCB.Processing';
        default:
            return '';
    }
}
